import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { WaypointType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import Label from '~components/FormFields/v2/Label';
import { createLabeledWaypointSvgDataUrl } from '~pages/Admin/Geo/mapIcons';

import {
  AI_SUMMARY_MAP_COLOR,
  AiSiteFocus,
  AiSummaryCircle,
  AiSummarySite,
  groupAiSummaryCirclesByName,
} from '../Geofence/aiSummaryCircles';
import { formatAiSummaryTimestamp } from './aiSummaryFormatters';

function sideWaypointIcon(sides: AiSummarySite['sides']) {
  if (sides.length > 1) return 'both' as const;

  return sides[0] === 'pickup' ? WaypointType.PICKUP : WaypointType.DROP_OFF;
}

/** Describes how many times a site was visited, calling out the role mix when it's uniform. */
function describeVisitCount(visits: AiSummarySite['visits']) {
  const pickupCount = visits.filter((visit) => visit.side === 'pickup').length;
  const dropoffCount = visits.filter((visit) => visit.side === 'dropoff').length;
  const isPickupOnly = pickupCount > 0 && dropoffCount === 0;
  const isDropoffOnly = dropoffCount > 0 && pickupCount === 0;

  if (isPickupOnly) {
    return t('admin.job_geo_debug.ai_summary_site_pickup_count', { count: pickupCount });
  }
  if (isDropoffOnly) {
    return t('admin.job_geo_debug.ai_summary_site_dropoff_count', {
      count: dropoffCount,
    });
  }
  return t('admin.job_geo_debug.ai_summary_site_visit_count', {
    count: pickupCount + dropoffCount,
  });
}

function SiteField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <FormFieldLabel label={label} sx={{ mb: 0.5 }} />
      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
        {value}
      </Typography>
    </Box>
  );
}

function VisitRow({ visit }: { visit: AiSummarySite['visits'][number] }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
        {t('admin.job_geo_debug.ai_summary_site_visit_label', {
          load: visit.loadNumber,
          side: visit.side,
        })}
      </Typography>
      <Typography variant="caption" noWrap>
        {formatAiSummaryTimestamp(visit.enteredAt)} –{' '}
        {formatAiSummaryTimestamp(visit.exitedAt)}
      </Typography>
    </Box>
  );
}

function GeofenceRow({
  circle,
  number,
}: {
  circle: AiSiteFocus['circles'][number];
  number: number;
}) {
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {t('admin.job_geo_debug.ai_summary_site_geofence_row', {
        number,
        radius: Math.round(circle.radiusM),
      })}
    </Typography>
  );
}

interface AiSummarySitesProps {
  circles: AiSummaryCircle[];
  expandedSiteKey: string | null;
  onToggleSite: (site: AiSiteFocus | null) => void;
}

export function AiSummarySites({
  circles,
  expandedSiteKey,
  onToggleSite,
}: AiSummarySitesProps) {
  const aiSites = useMemo(() => groupAiSummaryCirclesByName(circles), [circles]);

  if (aiSites.length === 0) {
    return null;
  }

  return (
    <Box>
      <Label sx={{ mb: 0.5, pl: '9px' }}>
        {t('admin.job_geo_debug.ai_summary_ai_sites_label')}
      </Label>
      {aiSites.map((aiSite) => (
        <Accordion
          key={aiSite.key}
          expanded={expandedSiteKey === aiSite.key}
          onChange={(_, expanded) => {
            onToggleSite(expanded ? { key: aiSite.key, circles: aiSite.circles } : null);
          }}
          disableGutters
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            border: (theme) => `1px solid ${theme.palette.divider}`,
            '&:not(:first-of-type)': { borderTop: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              minHeight: 36,
              px: 1,
              '& .MuiAccordionSummary-content': {
                my: 0.5,
                minWidth: 0,
                overflow: 'hidden',
                alignItems: 'center',
                gap: 0.75,
              },
            }}
          >
            <img
              src={createLabeledWaypointSvgDataUrl(
                sideWaypointIcon(aiSite.sides),
                AI_SUMMARY_MAP_COLOR,
              )}
              alt=""
              width={18}
              height={18}
              style={{ flexShrink: 0 }}
            />
            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
              {aiSite.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
              {describeVisitCount(aiSite.visits)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              pt: 0,
              pb: 1,
              px: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <SiteField
              label={t('admin.job_geo_debug.ai_summary_site_source_label')}
              value={
                aiSite.isConfiguredSite
                  ? t('admin.job_geo_debug.ai_summary_circle_configured')
                  : t('admin.job_geo_debug.ai_summary_circle_discovered')
              }
            />
            <Box>
              <FormFieldLabel
                label={t('admin.job_geo_debug.geofences_label')}
                sx={{ mb: 0.5 }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {aiSite.circles.map((circle, index) => (
                  <GeofenceRow key={index} circle={circle} number={index + 1} />
                ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {aiSite.visits.map((visit) => (
                <VisitRow key={`${visit.loadNumber}-${visit.side}`} visit={visit} />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
